import "./EmailLoginForm.scss";

import { type FC, ReactNode, useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";

import { emailMustBeValid, emailMustHaveAtSign } from "../../helpers/password-errors";

import { EulaError, serverLogin } from "api/api-server-actions";
import { LOGO_FORM_SIZE } from "components/constants";
import { ErrorMessageBanner } from "components/forms/ErrorMessageBanner";
import { t } from "i18n/i18n";
import { LegacyButton } from "mds/components/LegacyButton";
import { TrackedInput } from "mds/components/TrackedInput";
import { TrackedLink } from "mds/components/TrackedLink";
import { ForumLogoIcon } from "mds/icons";
import { useNavigate } from "utils/routing";

interface EmailLoginFormValue {
  email: string;
  password: string;
}

interface EmailLoginFormProps {
  setIsLoggedIn: (isLoggedIn: boolean) => void;
  children?: ReactNode | ReactNode[];
}

export const EmailLoginForm: FC<EmailLoginFormProps> = ({ setIsLoggedIn, children }) => {
  const [passwordResetMessage, setPasswordResetMessage] = useState<boolean>(false);
  const form = useForm<EmailLoginFormValue>();
  const navigate = useNavigate();

  const handleSubmit: SubmitHandler<EmailLoginFormValue> = (formData) => {
    serverLogin(formData.email, formData.password)
      .then(() => {
        setIsLoggedIn(true);
      })
      .catch((error) => {
        if (
          error instanceof Error &&
          error.name === "EULA_NOT_ACCEPTED" &&
          (error as EulaError).token &&
          (error as EulaError).orgId
        ) {
          // Redirect to onboarding with token and org_id
          const eulaError = error as EulaError;
          navigate(`/onboarding-invite?token=${eulaError.token}&org_id=${eulaError.orgId}`);
        } else if (error instanceof Error && error.name === "EULA_NOT_ACCEPTED") {
          form.setError("email", {
            type: "server",
            message: t("error.org_not_found"),
          });
        } else {
          form.setError("password", {
            type: "server",
            message: t("error.incorrect_email_or_password"),
          });
        }
      });
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.get("password_reset") === "true") {
      setPasswordResetMessage(true);
    }
  }, []);

  return (
    <div className="m-2 w-full max-w-[18rem] p-4 md:w-96">
      <div className="mb-3 flex items-center justify-between border-b border-black-tint-90 bg-white pb-1 text-black-tint-20">
        <span className="mb-2 flex cursor-pointer items-center">
          <ForumLogoIcon width={LOGO_FORM_SIZE} />
        </span>
      </div>

      <h2 className="mb-3">{t("user_info.login_title")}</h2>

      {passwordResetMessage && (
        <span className="mb-2 flex cursor-pointer">
          <p className=" ">{t("user_info.login_after_password_reset")}</p>
        </span>
      )}

      <FormProvider {...form}>
        <form
          className="mb-3 flex w-full flex-col gap-3"
          noValidate
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <div>
            <label>
              <div className="body-m-bold">{t("user_info.email")}</div>

              <TrackedInput
                {...form.register("email", {
                  required: t("error.field_required"),
                  validate: {
                    emailMustHaveAtSign,
                    emailMustBeValid,
                  },
                })}
                autoComplete="email"
                className="email-login-form-input"
                eventName="Account - Sign in / Email"
                eventPropertyKey="userEmail"
                id="email"
                type="email"
              />
            </label>

            <ErrorMessageBanner className="body-s mt-1 w-full text-wrap break-all" name="email" />
          </div>

          <div>
            <label>
              <div className="body-m-bold">{t("user_info.password")}</div>

              <TrackedInput
                {...form.register("password", {
                  required: t("error.field_required"),
                })}
                autoComplete="current-password"
                className="email-login-form-input"
                eventName="Account - Sign in / Password"
                id="password"
                type="password"
              />
            </label>

            <ErrorMessageBanner className="body-s mt-1 w-full" name="password" />
          </div>

          {/* This invisible field is required for LastPass to recognize the autofill */}
          <input className="sr-only" type="submit" value="Login" />

          <LegacyButton
            className="mt-3"
            eventName="Account - Sign in / Confirm"
            kind="primary"
            type="submit"
            value="Login"
          >
            {t("user_info.sign_in")}
          </LegacyButton>

          {children}
        </form>
      </FormProvider>

      <TrackedLink
        className="body-s mb-2 mt-8 block cursor-pointer text-black-tint-40 underline"
        eventName="Account - Sign in / Forgot password"
        id="forgot-password"
        to="/forgot-password"
      >
        {t("user_info.forgot_password")}
      </TrackedLink>
    </div>
  );
};
